/**
 * @file    styles.js
 * @author  Daniel Naftzger
 * @created 2026-02-18
 * @updated 2026-03-05
 *
 * @description
 * These are the css styles for the home page. They are in a JS object 
 * because of how they are called upon when styles need to change. 
 * The functions that change shading/translate the project cards expect this format. 
 *
 */

const styles = {
  root: {
    fontFamily: "'Syne', sans-serif",
    color: "#e8e8e8",
    background: "#0a0a0a",
    minHeight: "100vh",
    position: "relative",
  },
  gridBg: {
    position: "fixed",
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(232,255,71,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(232,255,71,0.03) 1px, transparent 1px)
    `,
    backgroundSize: "60px 60px",
    pointerEvents: "none",
    zIndex: 0,
  },
  nav: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1.25rem 3rem",
    transition: "all 0.3s ease",
  },
  navScrolled: {
    background: "rgba(10,10,10,0.9)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid #1a1a1a",
  },
  logo: {
    fontWeight: 800,
    fontSize: "1.1rem",
    letterSpacing: "0.1em",
    color: "#e8ff47",
    cursor: "pointer",
  },
  navLinks: {
    display: "flex",
    gap: "0.25rem",
  },
  navBtn: {
    padding: "0.4rem 1rem",
    fontSize: "0.8rem",
    fontFamily: "'DM Mono', monospace",
    color: "#888",
    letterSpacing: "0.05em",
    borderRadius: "4px",
    transition: "all 0.15s ease",
  },
  navBtnActive: {
    color: "#e8ff47",
    background: "rgba(232,255,71,0.08)",
  },
  main: {
    position: "relative",
    zIndex: 1,
    maxWidth: "900px",
    margin: "0 auto",
    padding: "0 2rem",
  },
  hero: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingTop: "5rem",
    paddingBottom: "4rem",
  },
  heroTag: {
    display: "inline-block",
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    color: "#e8ff47",
    background: "rgba(232,255,71,0.1)",
    border: "1px solid rgba(232,255,71,0.3)",
    padding: "0.3rem 0.75rem",
    borderRadius: "100px",
    marginBottom: "2rem",
    letterSpacing: "0.08em",
    width: "fit-content",
  },
  heroName: {
    fontSize: "clamp(3rem, 8vw, 6rem)",
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: "-0.02em",
    marginBottom: "0.5rem",
  },
  heroTitle: {
    fontSize: "clamp(1.2rem, 3vw, 1.8rem)",
    fontWeight: 400,
    color: "#555",
    marginBottom: "1.5rem",
  },
  heroTagline: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "1rem",
    color: "#e8ff47",
    marginBottom: "1.5rem",
    fontStyle: "italic",
  },
  heroBio: {
    maxWidth: "580px",
    color: "#888",
    lineHeight: 1.8,
    fontSize: "1rem",
    marginBottom: "2.5rem",
  },
  heroCtas: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
  },
  btnPrimary: {
    background: "#e8ff47",
    color: "#0a0a0a",
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    fontSize: "0.85rem",
    padding: "0.75rem 1.75rem",
    borderRadius: "6px",
    letterSpacing: "0.03em",
    transition: "opacity 0.15s ease",
  },
  btnSecondary: {
    border: "1px solid #333",
    color: "#ccc",
    fontFamily: "'Syne', sans-serif",
    fontWeight: 600,
    fontSize: "0.85rem",
    padding: "0.75rem 1.75rem",
    borderRadius: "6px",
    letterSpacing: "0.03em",
    transition: "border-color 0.15s ease",
  },
  section: {
    paddingTop: "6rem",
    paddingBottom: "2rem",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: "1rem",
    marginBottom: "3rem",
    borderBottom: "1px solid #1a1a1a",
    paddingBottom: "1rem",
  },
  sectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    color: "#444",
    letterSpacing: "0.1em",
  },
  sectionTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    letterSpacing: "-0.02em",
  },
  projectGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "1rem",
  },
  projectCard: {
    display: "flex",
    flexDirection: "column",
    background: "#111",
    border: "1px solid #1e1e1e",
    borderRadius: "10px",
    padding: "1.75rem",
    cursor: "pointer",
  },
  projectTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  projectYear: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    color: "#444",
  },
  projArrow: {
    fontSize: "1.1rem",
    color: "#e8ff47",
  },
  projectName: {
    fontSize: "1.25rem",
    fontWeight: 700,
    marginBottom: "0.75rem",
    letterSpacing: "-0.01em",
  },
  projectDesc: {
    fontSize: "0.875rem",
    color: "#777",
    lineHeight: 1.7,
    marginBottom: "1.25rem",
  },
  projectTags: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    marginBottom: "1.25rem",
  },
  projectTag: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.65rem",
    color: "#e8ff47",
    background: "rgba(232,255,71,0.08)",
    padding: "0.2rem 0.6rem",
    borderRadius: "4px",
    letterSpacing: "0.05em",
  },
  skillsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "2rem",
  },
  skillGroup: {},
  skillCategory: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    color: "#e8ff47",
    letterSpacing: "0.1em",
    marginBottom: "1rem",
    textTransform: "uppercase",
  },
  skillItems: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  skillChip: {
    fontSize: "0.9rem",
    color: "#ccc",
    fontWeight: 600,
  },
  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    borderLeft: "1px solid #1e1e1e",
    paddingLeft: "2rem",
    marginLeft: "0.5rem",
  },
  timelineItem: {
    position: "relative",
    paddingBottom: "2.5rem",
  },
  timelineDot: {
    position: "absolute",
    left: "-2.5rem",
    top: "0.4rem",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "#e8ff47",
    border: "2px solid #0a0a0a",
    outline: "1px solid #e8ff47",
  },
  timelineContent: {},
  timelineHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: "0.25rem",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  timelineRole: {
    fontSize: "1.05rem",
    fontWeight: 700,
  },
  timelinePeriod: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    color: "#555",
  },
  timelineCompany: {
    color: "#e8ff47",
    fontSize: "0.85rem",
    fontWeight: 600,
    marginBottom: "0.75rem",
  },
  timelineBullets: {
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
  },
  timelineBullet: {
    fontSize: "0.875rem",
    color: "#777",
    lineHeight: 1.6,
    paddingLeft: "1rem",
    position: "relative",
  },
  contactBox: {
    background: "#111",
    border: "1px solid #1e1e1e",
    borderRadius: "12px",
    padding: "3rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "1.25rem",
  },
  contactIntro: {
    color: "#888",
    lineHeight: 1.8,
    maxWidth: "480px",
  },
  contactEmail: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "1.1rem",
    color: "#e8ff47",
    borderBottom: "1px solid rgba(232,255,71,0.3)",
    paddingBottom: "2px",
  },
  contactLinks: {
    display: "flex",
    gap: "1.5rem",
  },
  contactLink: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.8rem",
    color: "#666",
    transition: "color 0.15s ease",
  },
  hamburger: {
    display: "none",
    flexDirection: "column",
    justifyContent: "center",
    gap: "5px",
    cursor: "pointer",
    padding: "6px",
    background: "none",
    border: "none",
    zIndex: 101,
  },
  hamburgerLine: {
    width: "22px",
    height: "2px",
    background: "#e8e8e8",
    borderRadius: "2px",
    transition: "all 0.3s ease",
    display: "block",
  },
  mobileMenuOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10,10,10,0.97)",
    zIndex: 99,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2.5rem",
  },
  mobileMenuBtn: {
    fontSize: "1.75rem",
    fontFamily: "'Syne', sans-serif",
    fontWeight: 800,
    color: "#e8e8e8",
    background: "none",
    border: "none",
    cursor: "pointer",
    letterSpacing: "-0.01em",
    transition: "color 0.15s ease",
  },
  footer: {
    marginTop: "6rem",
    paddingTop: "2rem",
    paddingBottom: "3rem",
    borderTop: "1px solid #1a1a1a",
    display: "flex",
    justifyContent: "space-between",
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.75rem",
    color: "#444",
  },

  /* ═══════════════════════════════════════════════════════════════
     Architecture Button (on ProjectCard)
     ═══════════════════════════════════════════════════════════════ */
  archBtn: {
    marginTop: "auto",
    alignSelf: "flex-start",
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.72rem",
    color: "#e8ff47",
    background: "transparent",
    border: "1px solid rgba(232,255,71,0.3)",
    borderRadius: "6px",
    padding: "0.4rem 0.85rem",
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "all 0.15s ease",
  },

  /* ═══════════════════════════════════════════════════════════════
     Architecture Modal (ProjectArchModal.js)
     ═══════════════════════════════════════════════════════════════ */
  archOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",
  },
  archModal: {
    position: "relative",
    background: "#131316",
    border: "1px solid #1e1e1e",
    borderRadius: "14px",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 0,
  },
  archModalHeader: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#131316",
    padding: "1.5rem 1.75rem 1rem",
    borderBottom: "1px solid #1e1e1e",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1rem",
  },
  archModalTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    lineHeight: 1.3,
  },
  archModalYear: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.65rem",
    color: "#555",
    marginTop: "0.35rem",
  },
  archCloseBtn: {
    flexShrink: 0,
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #2a2a2a",
    borderRadius: "6px",
    color: "#888",
    background: "transparent",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.15s ease",
  },
  archModalBody: {
    padding: "1.5rem 1.75rem 2rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.75rem",
  },
  archSummary: {
    fontSize: "0.9rem",
    color: "#888",
    lineHeight: 1.75,
  },
  archSectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.65rem",
    color: "#e8ff47",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "1rem",
  },
  archLayerCard: {
    background: "rgba(232,255,71,0.02)",
    border: "1px solid #1e1e1e",
    borderRadius: "8px",
    padding: "1.15rem 1.25rem",
    marginBottom: "0.65rem",
  },
  archLayerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.6rem",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  archLayerName: {
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  archLayerTech: {
    display: "flex",
    gap: "0.4rem",
    flexWrap: "wrap",
  },
  archLayerTag: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.6rem",
    color: "#e8ff47",
    background: "rgba(232,255,71,0.1)",
    padding: "0.15rem 0.5rem",
    borderRadius: "3px",
    letterSpacing: "0.04em",
  },
  archLayerDetail: {
    fontSize: "0.84rem",
    color: "#777",
    lineHeight: 1.7,
  },
  archFlowList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    position: "relative",
  },
  archFlowStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.85rem",
    position: "relative",
    paddingBottom: "0.9rem",
  },
  archFlowIndex: {
    flexShrink: 0,
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    background: "rgba(232,255,71,0.12)",
    color: "#e8ff47",
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.6rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
  },
  archFlowConnector: {
    position: "absolute",
    left: "10px",
    top: "22px",
    width: "1px",
    bottom: 0,
    background: "#2a2a2a",
  },
  archFlowText: {
    fontSize: "0.84rem",
    color: "#aaa",
    lineHeight: 1.6,
    paddingTop: "0.1rem",
  },
};

export default styles;